import React, {ComponentType, lazy} from "react";

/**
 * A wrapper around React.lazy that adds retry logic and auto-reload for chunk load failures.
 *
 * When a chunk fails to load (common after deployments when old chunks are replaced):
 * 1. It retries loading the chunk up to 3 times with exponential backoff
 * 2. If all retries fail, it stores a flag in sessionStorage and reloads the page
 * 3. After reload, if the flag is present, it clears caches and tries again
 *
 * This prevents users from seeing ChunkLoadError after deployments.
 */

const RELOAD_FLAG_KEY = "chunk_reload_attempted";
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

// Check if error is a chunk load error
function isChunkLoadError(error: unknown): boolean {
	if (error instanceof Error) {
		return (
			error.name === "ChunkLoadError" ||
			error.message.includes("Loading chunk") ||
			error.message.includes("Loading CSS chunk") ||
			error.message.includes("Failed to fetch dynamically imported module")
		);
	}

	return false;
}

// Clear all caches
async function clearCaches(): Promise<void> {
	if ("caches" in window) {
		try {
			const cacheNames = await caches.keys();
			await Promise.all(cacheNames.map((name) => caches.delete(name)));
		} catch (e) {
			console.warn("Failed to clear caches:", e);
		}
	}
}

// Sleep utility
function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wraps a dynamic import with retry logic
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends ComponentType<any>>(
	componentImport: () => Promise<{default: T}>,
	componentName?: string,
): React.LazyExoticComponent<T> {
	return lazy(async () => {
		// Check if we already tried reloading for this session
		const hasReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY);

		// If we've already reloaded once, clear the flag and try fresh
		if (hasReloaded) {
			sessionStorage.removeItem(RELOAD_FLAG_KEY);
			await clearCaches();
		}

		let lastError: unknown;

		for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
			try {
				const component = await componentImport();

				return component;
			} catch (error) {
				lastError = error;

				if (isChunkLoadError(error)) {
					console.warn(
						`[lazyWithRetry] Chunk load failed for ${
							componentName || "component"
						} (attempt ${attempt}/${MAX_RETRIES})`,
						error,
					);

					if (attempt < MAX_RETRIES) {
						// Wait before retrying with exponential backoff
						await sleep(RETRY_DELAY_MS * attempt);
						// Clear caches before retry
						await clearCaches();
					}
				} else {
					// Non-chunk error, throw immediately
					throw error;
				}
			}
		}

		// All retries failed - trigger page reload
		if (!hasReloaded && isChunkLoadError(lastError)) {
			console.warn("[lazyWithRetry] All retries failed, reloading page...");
			sessionStorage.setItem(RELOAD_FLAG_KEY, "true");

			// Force a hard reload bypassing cache
			window.location.reload();

			// Return a never-resolving promise to prevent React from rendering an error
			// while the page is reloading
			return new Promise<{default: T}>(() => {
				// Intentionally empty - page is reloading
			});
		}

		// If we've already reloaded and still failing, throw the error
		throw lastError;
	});
}

export default lazyWithRetry;
export {clearCaches, isChunkLoadError};
